<script setup lang="ts">
import { VIcons } from '@/shared/ui'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types.ts'
import { type PropType, ref, onBeforeUnmount } from 'vue'

const ROW_CELLS = 12
const MIN_LENGTH = 2

const props = defineProps({
  cellElem: {
    type: Object as PropType<null | HTMLElement>,
    default: null,
  },
})

const gridFields = defineModel<GridFieldType[]>('gridFields', { required: true })
const editField = defineModel<string | null>('editField', { default: null })
const defaultLen = defineModel<number>('defaultLen')

/**
 * Функция выделения/снятия выделения по клику на объекте
 * @param id
 */
const editFieldHandler = (id: string) => {
  editField.value = editField.value === id ? null : id
}

/**
 * Получаем стили для поля
 * @param field
 */
const getFieldStyle = (field: GridFieldType) => {
  if (!props.cellElem) return {}

  const cellRect = props.cellElem.getBoundingClientRect()

  const fieldWidth = field.length * cellRect.width + (field.length - 1) * 4
  const topField = field.row * 40 + field.row * 4 + 4
  const leftField = field.cell * cellRect.width + field.cell * 4 + 4

  return {
    width: fieldWidth + 'px',
    top: topField + 'px',
    left: leftField + 'px',
  }
}

/** Resize via pointer events (HTML5 drag does not provide clientX during drag) */
const resizingFieldId = ref<string | null>(null)
let startX = 0
let startLen = 0

const resizePointerMove = (field: GridFieldType, event: PointerEvent) => {
  if (!props.cellElem) return

  const cellRect = props.cellElem.getBoundingClientRect()
  const dif = event.clientX - startX
  const difCells = Math.round(dif / (cellRect.width + 4))
  const maxLength = ROW_CELLS - field.cell
  const newLen = Math.max(MIN_LENGTH, Math.min(maxLength, startLen + difCells))

  field.length = newLen
}

const resizePointerUp = (
  field: GridFieldType,
  handleEl: HTMLElement,
  moveHandler: (e: PointerEvent) => void,
  upHandler: () => void,
) => {
  handleEl.classList.remove('invent-pass-grid__transform_hide')
  resizingFieldId.value = null
  defaultLen.value = 4

  document.removeEventListener('pointermove', moveHandler)
  document.removeEventListener('pointerup', upHandler)
}

let boundMove: ((e: PointerEvent) => void) | undefined
let boundUp: (() => void) | undefined

const resizePointerDown = (field: GridFieldType, event: PointerEvent) => {
  if (!event.currentTarget) return

  event.preventDefault()
  resizingFieldId.value = field.id
  startX = event.clientX
  startLen = field.length
  defaultLen.value = field.length

  const handleEl = event.currentTarget as HTMLElement
  handleEl.classList.add('invent-pass-grid__transform_hide')

  boundMove = (e: PointerEvent) => resizePointerMove(field, e)
  boundUp = () => resizePointerUp(field, handleEl, boundMove!, boundUp!)

  document.addEventListener('pointermove', boundMove)
  document.addEventListener('pointerup', boundUp)
}

onBeforeUnmount(() => {
  if (boundMove) document.removeEventListener('pointermove', boundMove)
  if (boundUp) document.removeEventListener('pointerup', boundUp)
})

/** Функции перемещения поля */

/**
 * Функция старта перемещения
 * @param field
 */
const dragMoveStartHandler = (field: GridFieldType) => {
  defaultLen.value = field.length
}

/**
 * Функция окончания перетаскивания
 */
const dragMoveEndHandler = () => {
  defaultLen.value = 4
}

const isHeading = (field: GridFieldType) => field.type === 'heading'
</script>

<template>
  <div
    v-for="field in gridFields"
    :key="field.id"
    class="invent-pass-grid__grid-area-field"
    :class="{
      'invent-pass-grid__grid-area-field_selected': editField === field.id,
    }"
    :style="getFieldStyle(field)"
    :draggable="editField === field.id && resizingFieldId !== field.id"
    @dragstart.stop="dragMoveStartHandler(field)"
    @dragend.stop="dragMoveEndHandler"
    @click.stop="editFieldHandler(field.id)"
  >
    <VIcons class="invent-pass-grid__translate" type="more_vert" />

    <template v-if="isHeading(field)">
      <VTextField
        v-if="editField === field.id"
        v-model="field.name"
        hide-details
        density="compact"
        class="invent-pass-grid__heading-input"
        @click.stop
      />
      <strong v-else class="invent-pass-grid__heading-text">{{ field.name }}</strong>
    </template>
    <template v-else>
      <VTooltip :text="field.name" :open-delay="1000">
        <template #activator="{ props }">
          <span v-bind="props" class="invent-pass-grid__grid-area-field-label">{{ field.name }}</span>
        </template>
      </VTooltip>
    </template>
    <!-- <template v-else>{{ field.name }}</template> -->

    <div
      class="invent-pass-grid__transform"
      @pointerdown.stop.prevent="resizePointerDown(field, $event)"
    ></div>
  </div>
</template>
